import React, { useEffect, Suspense, lazy, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AsyncTasksProvider } from './contexts/AsyncTasksContext';
import ErrorBoundary from './components/ErrorBoundary';
import UserbackInit from './UserbackInit';
import { colors } from './styles/colors';

// Loading spinner for lazy-loaded routes
const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.dark }}>
    <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: colors.lime, borderTopColor: 'transparent' }}></div>
  </div>
);

// Public/Auth pages
const LoginV2 = lazy(() => import('./pages/auth/LoginV2'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const ActivatePage = lazy(() => import('./pages/auth/ActivatePage'));
const InterviewResponse = lazy(() => import('./pages/auth/InterviewResponse'));
const StripeConnect = lazy(() => import('./pages/candidate/StripeConnect'));
const HubstaffSetup = lazy(() => import('./pages/candidate/HubstaffSetup'));
const KYCVerification = lazy(() => import('./pages/candidate/KYCVerification'));
const WaitlistPage = lazy(() => import('./pages/Waitlist'));
const AgendarPage = lazy(() => import('./pages/AgendarPage'));
const VideoSalesPage = lazy(() => import('./pages/VideoSalesPage'));
const ClientWaitlist = lazy(() => import('./pages/ClientWaitlist'));

// Landing principal: mientras NO aceptamos nuevos clientes, "/" muestra ClientWaitlist
// (formulario de lista de espera). Para reactivar la landing completa (VideoSalesPage),
// poner REACT_APP_ACCEPTING_CLIENTS=true en el .env del frontend y rebuild.
const ACCEPTING_NEW_CLIENTS = process.env.REACT_APP_ACCEPTING_CLIENTS === 'true';
const LeadFormPage = lazy(() => import('./pages/LeadFormPage'));
const JobsLanding = lazy(() => import('./pages/candidate/JobsLanding'));

// Lazy-loaded: Candidate public pages
const JobDetail = lazy(() => import('./pages/JobDetail'));
const ApplicationFunnel = lazy(() => import('./pages/candidate/ApplicationFunnel'));
const ApplicationSuccess = lazy(() => import('./pages/candidate/ApplicationSuccess'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// Lazy-loaded: Candidate portal
const CandidateDashboard = lazy(() => import('./pages/candidate/CandidateDashboard'));
const CandidateApplications = lazy(() => import('./pages/candidate/CandidateApplications'));
const CandidateApplicationDetail = lazy(() => import('./pages/candidate/CandidateApplicationDetail'));
const CandidateProfile = lazy(() => import('./pages/candidate/CandidateProfile'));
const CandidateHires = lazy(() => import('./pages/candidate/CandidateHires'));
const CandidateAvailability = lazy(() => import('./pages/candidate/CandidateAvailability'));
const CandidateOffers = lazy(() => import('./pages/candidate/CandidateOffers'));

// Lazy-loaded: Candidate blocks (Funnel 2.0)
const CVBlock = lazy(() => import('./pages/candidate/blocks/CVBlock'));
const TestTecnicoBlock = lazy(() => import('./pages/candidate/blocks/TestTecnicoBlock'));
const TestPersonalidadBlock = lazy(() => import('./pages/candidate/blocks/TestPersonalidadBlock'));
const EjercicioBlock = lazy(() => import('./pages/candidate/blocks/EjercicioBlock'));
const EntrevistaBlock = lazy(() => import('./pages/candidate/blocks/EntrevistaBlock'));
const Stage4EntrevistaBlock = lazy(() => import('./pages/candidate/blocks/Stage4EntrevistaBlock'));
const RevisionFinalBlock = lazy(() => import('./pages/candidate/blocks/RevisionFinalBlock'));

// Lazy-loaded: Recruiter pages
const RecruiterDashboard = lazy(() => import('./pages/recruiter/DashboardV2'));
const RecruiterRolesList = lazy(() => import('./pages/recruiter/RolesList'));
const RecruiterCreateRole = lazy(() => import('./pages/recruiter/CreateRole'));
const RecruiterRoleDetail = lazy(() => import('./pages/recruiter/RoleDetail'));
const RecruiterApplications = lazy(() => import('./pages/recruiter/ApplicationsV2'));
const RecruiterApplicationDetail = lazy(() => import('./pages/recruiter/ApplicationDetail'));
const RecruiterTalentPool = lazy(() => import('./pages/recruiter/TalentPoolV2'));
const RecruiterSolicitudes = lazy(() => import('./pages/recruiter/RecruiterSolicitudes'));
const ClientRequestDetail = lazy(() => import('./pages/recruiter/ClientRequestDetail'));
const AdminLayout = lazy(() => import('./components/layouts/AdminLayout'));
const RecruiterRolesCreating = lazy(() => import('./pages/recruiter/RolesCreating'));
const RecruiterSupport = lazy(() => import('./pages/recruiter/RecruiterSupport'));
const RecruiterInterviews = lazy(() => import('./pages/recruiter/RecruiterInterviews'));
const PlacementsPanel = lazy(() => import('./pages/recruiter/PlacementsPanel'));
const FinancialsPanel = lazy(() => import('./pages/recruiter/FinancialsPanel'));
const NegotiationsPanel = lazy(() => import('./pages/recruiter/NegotiationsPanel'));
const MediationPanel = lazy(() => import('./pages/recruiter/MediationPanel'));

// Lazy-loaded: Closer pages
const CloserDashboard = lazy(() => import('./pages/closer/CloserDashboard'));
const SalesCallsList = lazy(() => import('./pages/closer/SalesCallsList'));
const SalesCallDetail = lazy(() => import('./pages/closer/SalesCallDetail'));
const CloserCommissions = lazy(() => import('./pages/closer/CloserCommissions'));
const CloserAccount = lazy(() => import('./pages/closer/CloserAccount'));
const CloserCalculator = lazy(() => import('./pages/closer/CloserCalculator'));
const RecruiterCalculator = lazy(() => import('./pages/recruiter/RecruiterCalculator'));
const AdminCalculator = lazy(() => import('./pages/admin/AdminCalculator'));

// Lazy-loaded: Client pages
const ClientDashboard = lazy(() => import('./pages/client/DashboardV2'));
const ClientNewSearch = lazy(() => import('./pages/client/NewSearchV2'));
const ClientSearches = lazy(() => import('./pages/client/SearchesV2'));
const ClientCandidates = lazy(() => import('./pages/client/CandidatesV2'));
const SearchPayRedirect = lazy(() => import('./pages/client/SearchPayRedirect'));
const ClientHires = lazy(() => import('./pages/client/Hires'));
const MyTeam = lazy(() => import('./pages/client/MyTeam'));
const ClientBilling = lazy(() => import('./pages/client/BillingV2'));
const ClientPaymentSuccess = lazy(() => import('./pages/client/PaymentSuccess'));
const ClientInterviews = lazy(() => import('./pages/client/InterviewsV2'));
const ClientScheduleSession = lazy(() => import('./pages/client/ScheduleSession'));
const ClientSupport = lazy(() => import('./pages/client/ClientSupport'));
const ClientProfile = lazy(() => import('./pages/client/ClientProfile'));
const ClientOfferView = lazy(() => import('./pages/client/OfferView'));
const ClientContractSign = lazy(() => import('./pages/client/ContractSign'));
const CandidateOfferView = lazy(() => import('./pages/candidate/OfferView'));

// Lazy-loaded: Meeting pages
const MeetingRoom = lazy(() => import('./pages/meeting/MeetingRoom'));
const SalesCallRoom = lazy(() => import('./pages/meeting/SalesCallRoom'));
const WaitingRoom = lazy(() => import('./pages/meeting/WaitingRoom'));
const GuestJoin = lazy(() => import('./pages/meeting/GuestJoin'));
const OrbDemo = lazy(() => import('./pages/demo/OrbDemo'));

// Lazy-loaded: Admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminWaitlist = lazy(() => import('./pages/admin/AdminWaitlist'));
const AdminRoles = lazy(() => import('./pages/admin/AdminRoles'));
const AdminApplications = lazy(() => import('./pages/admin/AdminApplications'));
const AdminSolicitudes = lazy(() => import('./pages/admin/AdminSolicitudes'));
const AdminClients = lazy(() => import('./pages/admin/AdminClients'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCreateRole = lazy(() => import('./pages/admin/AdminCreateRole'));
const AdminRolesCreating = lazy(() => import('./pages/admin/AdminRolesCreating'));
const AlgorithmConfig = lazy(() => import('./pages/admin/AlgorithmConfig'));
const AdminCountries = lazy(() => import('./pages/admin/Countries'));
const AdminWhatsApp = lazy(() => import('./pages/admin/AdminWhatsApp'));
const AdminCommissions = lazy(() => import('./pages/admin/AdminCommissions'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminSalesCalls = lazy(() => import('./pages/admin/AdminSalesCalls'));
const AdminSupport = lazy(() => import('./pages/admin/AdminSupport'));
const AdminPrivacy = lazy(() => import('./pages/admin/AdminPrivacy'));
const SettingsPrivacy = lazy(() => import('./pages/settings/Privacy'));
const CloserViewPanel = lazy(() => import('./pages/admin/CloserViewPanel'));
const RecruiterMyTasks = lazy(() => import('./pages/recruiter/MyTasks'));

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.dark }}>
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: colors.lime, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Block unverified users — force email verification first
  if (user && user.is_verified === false) {
    return <Navigate to="/verify-email" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.rol)) {
    const portalRoutes = {
      admin: '/admin',
      reclutador: '/recruiter',
      cliente: '/client',
      candidato: '/candidate',
      empleado: '/employee'
    };
    return <Navigate to={portalRoutes[user?.rol] || '/login'} replace />;
  }
  
  return children;
};


// Placeholder for pages not yet built
const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.dark }}>
    <div className="bg-white rounded-3xl p-12 text-center">
      <h1 className="text-2xl font-bold mb-4" style={{ color: colors.dark }}>{title}</h1>
      <p className="text-slate-500">Esta página está en desarrollo</p>
    </div>
  </div>
);

function HomeRedirect() {
  const { user } = useAuth();
  const roleRoutes = {
    admin: '/admin',
    reclutador: '/recruiter',
    cliente: '/client',
    candidato: '/candidate/dashboard',
    empleado: '/employee/dashboard',
    closer: '/closer',
  };
  if (user?.rol && roleRoutes[user.rol]) {
    return <Navigate to={roleRoutes[user.rol]} replace />;
  }
  return ACCEPTING_NEW_CLIENTS ? <VideoSalesPage /> : <ClientWaitlist />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AsyncTasksProvider>
          <UserbackInit />
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:bg-white focus:text-slate-900 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium">
            Saltar al contenido
          </a>
          <ErrorBoundary>
          <Suspense fallback={<LazyFallback />}>
          <div id="main-content">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/agendar" element={<AgendarPage />} />
            <Route path="/login" element={<LoginV2 />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/activate/success" element={<ActivatePage />} />
            <Route path="/interview/accepted" element={<InterviewResponse />} />
            <Route path="/interview/rejected" element={<InterviewResponse />} />
            <Route path="/interview/already-responded" element={<InterviewResponse />} />

            {/* Public Job Routes */}
            <Route path="/jobs" element={<JobsLanding />} />
            <Route path="/jobs/:jobId" element={<JobDetail />} />
            <Route path="/apply/:roleId" element={<ApplicationFunnel />} />
            <Route path="/application-success" element={<ApplicationSuccess />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/video" element={<VideoSalesPage />} />
            <Route path="/empezar" element={<LeadFormPage />} />
            <Route path="/candidate/login" element={<Navigate to="/login" replace />} />
            <Route path="/candidate/register" element={<Navigate to="/register" replace />} />

          {/* Recruiter Routes */}
          <Route path="/recruiter" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterDashboard />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/roles" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterRolesList />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/calculadora" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterCalculator />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/roles/new" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterCreateRole />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/roles/creating" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterRolesCreating />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/roles/:id" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterRoleDetail />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/applications" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterApplications />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/applications/:id" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterApplicationDetail />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/talent-pool" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterTalentPool />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/placements" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <PlacementsPanel />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/financials" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <FinancialsPanel />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/client-requests" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterSolicitudes />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/client-requests/:requestId" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <ClientRequestDetail />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/interviews" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterInterviews />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/my-tasks" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterMyTasks />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/support" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <RecruiterSupport />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/negotiations" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <NegotiationsPanel />
            </ProtectedRoute>
          } />
          <Route path="/recruiter/offers/:offerId/mediate" element={
            <ProtectedRoute allowedRoles={['reclutador', 'admin']}>
              <MediationPanel />
            </ProtectedRoute>
          } />

          {/* Meeting Routes — intentionally public (no ProtectedRoute).
              Auth is handled inside each component:
              - MeetingRoom: requires user token OR guest_token
              - WaitingRoom: requires user token
              - GuestJoin: requires ?invite= query param, validated by backend */}
          <Route path="/meeting/:sessionId" element={<MeetingRoom />} />
          <Route path="/sales-call/:callId" element={<MeetingRoom mode="sales-call" />} />
          <Route path="/meeting/:sessionId/waiting" element={<WaitingRoom />} />
          <Route path="/meeting/:sessionId/join" element={<GuestJoin />} />

          {/* Dev demo routes */}
          <Route path="/demo/orb" element={<OrbDemo />} />

          {/* Client Routes */}
          <Route path="/client" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/client/new-search" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientNewSearch />
            </ProtectedRoute>
          } />
          <Route path="/client/searches" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientSearches />
            </ProtectedRoute>
          } />
          <Route path="/client/searches/:requestId/pay" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <SearchPayRedirect />
            </ProtectedRoute>
          } />
          <Route path="/client/searches/:requestId/candidates" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientCandidates />
            </ProtectedRoute>
          } />
          <Route path="/client/candidates" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientCandidates />
            </ProtectedRoute>
          } />
          <Route path="/client/offers/:offerId" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientOfferView />
            </ProtectedRoute>
          } />
          <Route path="/client/billing" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientBilling />
            </ProtectedRoute>
          } />
          <Route path="/client/payment-success" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientPaymentSuccess />
            </ProtectedRoute>
          } />
          <Route path="/client/contract/:negotiationId" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientContractSign />
            </ProtectedRoute>
          } />
          <Route path="/client/hires" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientHires />
            </ProtectedRoute>
          } />
          <Route path="/client/profile" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientProfile />
            </ProtectedRoute>
          } />
          <Route path="/client/team" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <MyTeam />
            </ProtectedRoute>
          } />
          <Route path="/client/interviews" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientInterviews />
            </ProtectedRoute>
          } />
          <Route path="/client/interviews/:sessionId" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientInterviews />
            </ProtectedRoute>
          } />
          <Route path="/client/schedule/:requestId" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientScheduleSession />
            </ProtectedRoute>
          } />
          <Route path="/client/support" element={
            <ProtectedRoute allowedRoles={['cliente']}>
              <ClientSupport />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/team" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminTeam />
            </ProtectedRoute>
          } />
          <Route path="/admin/waitlist" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminWaitlist />
            </ProtectedRoute>
          } />
          <Route path="/admin/roles" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRoles />
            </ProtectedRoute>
          } />
          <Route path="/admin/calculadora" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCalculator />
            </ProtectedRoute>
          } />
          <Route path="/admin/roles/new" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCreateRole />
            </ProtectedRoute>
          } />
          <Route path="/admin/roles/creating" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminRolesCreating />
            </ProtectedRoute>
          } />
          <Route path="/admin/roles/:id" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RecruiterRoleDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/applications" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminApplications />
            </ProtectedRoute>
          } />
          <Route path="/admin/client-requests" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSolicitudes />
            </ProtectedRoute>
          } />
          <Route path="/admin/client-requests/:requestId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ClientRequestDetail Layout={AdminLayout} basePath="/admin" />
            </ProtectedRoute>
          } />
          <Route path="/admin/applications/:id" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <RecruiterApplicationDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/clients" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminClients />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/algorithm" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AlgorithmConfig />
            </ProtectedRoute>
          } />
          <Route path="/admin/countries" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCountries />
            </ProtectedRoute>
          } />
          <Route path="/admin/whatsapp" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminWhatsApp />
            </ProtectedRoute>
          } />
          <Route path="/admin/commissions" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCommissions />
            </ProtectedRoute>
          } />
          <Route path="/admin/notifications" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminNotifications />
            </ProtectedRoute>
          } />
          <Route path="/admin/closers/:closerId/view-panel" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CloserViewPanel />
            </ProtectedRoute>
          } />
          <Route path="/admin/sales-calls" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSalesCalls />
            </ProtectedRoute>
          } />
          <Route path="/admin/support" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSupport />
            </ProtectedRoute>
          } />
          <Route path="/admin/privacy" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPrivacy />
            </ProtectedRoute>
          } />

          {/* Settings Routes (all roles) */}
          <Route path="/settings/privacy" element={
            <ProtectedRoute allowedRoles={['cliente', 'candidato', 'reclutador', 'closer', 'admin']}>
              <SettingsPrivacy />
            </ProtectedRoute>
          } />

          {/* Closer Routes */}
          <Route path="/closer" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <CloserDashboard />
            </ProtectedRoute>
          } />
          <Route path="/closer/sales-calls" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <SalesCallsList />
            </ProtectedRoute>
          } />
          <Route path="/closer/sales-calls/:callId" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <SalesCallDetail />
            </ProtectedRoute>
          } />
          <Route path="/closer/calculadora" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <CloserCalculator />
            </ProtectedRoute>
          } />
          <Route path="/closer/commissions" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <CloserCommissions />
            </ProtectedRoute>
          } />
          <Route path="/closer/account" element={
            <ProtectedRoute allowedRoles={['closer']}>
              <CloserAccount />
            </ProtectedRoute>
          } />

          {/* Employee Routes */}
          <Route path="/employee" element={
            <ProtectedRoute allowedRoles={['empleado']}>
              <PlaceholderPage title="Portal del Empleado" />
            </ProtectedRoute>
          } />

          {/* Candidate Portal */}
          {/* Candidate Portal Routes */}
          <Route path="/candidate/dashboard" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CandidateApplications />
            </ProtectedRoute>
          } />
          <Route path="/candidate" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CandidateApplications />
            </ProtectedRoute>
          } />
          <Route path="/candidate/application/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CandidateDashboard />
            </ProtectedRoute>
          } />
          <Route path="/candidate/application" element={
            <Navigate to="/candidate/dashboard" replace />
          } />
          <Route path="/candidate/applications" element={
            <Navigate to="/candidate/dashboard" replace />
          } />
          <Route path="/candidate/applications/:id" element={
            <Navigate to="/candidate/dashboard" replace />
          } />
          <Route path="/candidate/profile" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CandidateProfile />
            </ProtectedRoute>
          } />
          <Route path="/candidate/hires" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <CandidateHires />
            </ProtectedRoute>
          } />
          <Route path="/candidate/offers" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <CandidateOffers />
            </ProtectedRoute>
          } />
          <Route path="/candidate/offers/:offerId" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <CandidateOfferView />
            </ProtectedRoute>
          } />
          <Route path="/candidate/kyc" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <KYCVerification />
            </ProtectedRoute>
          } />
          <Route path="/candidate/stripe-connect" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <StripeConnect />
            </ProtectedRoute>
          } />
          <Route path="/candidate/hubstaff-setup" element={
            <ProtectedRoute allowedRoles={['candidato']}>
              <HubstaffSetup />
            </ProtectedRoute>
          } />
          <Route path="/candidate/availability" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CandidateAvailability />
            </ProtectedRoute>
          } />

          {/* Candidate Block Routes (Funnel 2.0) */}
          <Route path="/candidate/block/cv/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <CVBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/test-tecnico/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <TestTecnicoBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/test-personalidad/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <TestPersonalidadBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/ejercicio/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <EjercicioBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/entrevista/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <EntrevistaBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/entrevista-lucia/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <Stage4EntrevistaBlock />
            </ProtectedRoute>
          } />
          <Route path="/candidate/block/revision/:applicationId" element={
            <ProtectedRoute allowedRoles={['candidato', 'admin']}>
              <RevisionFinalBlock />
            </ProtectedRoute>
          } />

          {/* Legacy redirects */}
          <Route path="/v2/*" element={<Navigate to="/" replace />} />
          <Route path="/alternativa5" element={<Navigate to="/" replace />} />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </div>
          </Suspense>
          </ErrorBoundary>
        <Toaster position="top-right" richColors />
        </AsyncTasksProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
